import { Button, Grid, GridItem, Input, Select, Text, Textarea } from '@cloudflare/kumo'
import { PaperPlaneTilt, Plus, Trash } from '@phosphor-icons/react'
import {
  FACTORY_CLOSED_STATUSES,
  QUOTE_FEE_FREQUENCIES,
  QUOTE_SAMPLE_AVAILABILITY,
  findFactoryByName,
  rankFactoriesForRequest,
  type AdminQuoteView,
  type FactoryRequestMatch,
  type FactoryView,
  type QuoteFeeFrequency,
  type QuoteResult,
  type QuoteSampleAvailability,
} from '@repo/shared'
import { useState } from 'react'
import { FACTORY_STATUS_LABEL, FREQ_LABEL, SAMPLE_LABEL } from '../lib/quote-labels'

/** 자주 나오는 비용 항목 (Mad Will's 개발비 구조 + USDA 스타트업 비용표) */
const LINE_PRESETS: { label: string; frequency: QuoteFeeFrequency }[] = [
  { label: '개발비 (첫 SKU)', frequency: 'one_time' },
  { label: '테스트 배치', frequency: 'per_batch' },
  { label: '계약금 (환불 불가)', frequency: 'one_time' },
  { label: '라벨 동판·인쇄 세팅', frequency: 'one_time' },
  { label: '영양성분 분석', frequency: 'one_time' },
  { label: '유통기한 설정 실험', frequency: 'one_time' },
  { label: '팔레트 보관', frequency: 'monthly' },
  { label: '인증·심사', frequency: 'annual' },
]
const FREQ_ITEMS = QUOTE_FEE_FREQUENCIES.map((f) => ({ label: FREQ_LABEL[f], value: f }))
/** '' = 아직 모름 (sample=null). 앱 비교표에 "확인 중" 으로 보인다 */
const SAMPLE_ITEMS = [
  { label: '미입력', value: '' },
  ...QUOTE_SAMPLE_AVAILABILITY.map((a) => ({ label: SAMPLE_LABEL[a], value: a })),
]

/** 공장 고르기 칸의 한 줄: 요청과 맞는지를 앞에 붙여 맞는 공장이 눈에 띄게 한다 */
function factoryOptionLabel({
  factory,
  categoryMatch,
  storageMatch,
  missingCertifications,
}: FactoryRequestMatch<FactoryView>): string {
  const fit = [
    categoryMatch ? '분류 맞음' : '분류 다름',
    storageMatch === false ? '보관 안 맞음' : null,
    missingCertifications.length > 0 ? `인증 없음: ${missingCertifications.join(', ')}` : null,
  ]
  return [factory.name, factory.region, FACTORY_STATUS_LABEL[factory.status], ...fit]
    .filter(Boolean)
    .join(' · ')
}

/** 새 견적의 시작값 — 요청서의 통화·수량·설명을 이어받는다 */
export function emptyResult(q: AdminQuoteView): QuoteResult {
  return {
    factoryName: '',
    factoryLocation: '',
    factoryCertifications: [],
    currency: q.request.currency,
    unitPrice: 0,
    moq: q.request.quantity,
    casePack: 0,
    orderUnit: '',
    leadTimeDays: 30,
    validUntil: null,
    suggestedRetail: null,
    paymentTerms: '',
    barcode: '',
    dimensionsMm: null,
    taxRatePct: q.request.currency === 'KRW' ? 10 : 0,
    description: q.request.productDescription,
    sample: null,
    lines: [],
    notes: '',
  }
}

/** 공장 견적 입력 폼 — 商品企画御見積書 + 공동 포장 비용 구조의 공통 분모 */
export function OfferForm({
  form,
  setForm,
  editing,
  language,
  factories,
  request,
  busy,
  onSave,
  onCancel,
}: {
  form: QuoteResult
  setForm: (f: QuoteResult) => void
  editing: boolean
  language: string
  /** 공장 목록 (GET /admin/factories). 비어 있으면 고르는 칸 없이 공장명을 손으로 적는다 */
  factories: FactoryView[]
  request: AdminQuoteView['request']
  busy: boolean
  onSave: () => void
  onCancel: () => void
}) {
  // 인증은 입력 중인 글자를 그대로 들고 있는다 — 배열을 다시 합쳐 value 로 쓰면 방금 친 쉼표·공백이 지워진다
  const [certText, setCertText] = useState(() => form.factoryCertifications.join(', '))
  const ranked = rankFactoriesForRequest(factories, request)
  // 중단된 공장에 이미 이어진 견적을 고칠 때도 고른 값이 보이게 목록에 남긴다
  const linked = factories.find((f) => f.id === form.factoryId)
  const factoryItems = [
    { label: '목록에 없음 (공장명 직접 적기)', value: '' },
    ...ranked.map((m) => ({ label: factoryOptionLabel(m), value: m.factory.id })),
    ...(linked && FACTORY_CLOSED_STATUSES.includes(linked.status)
      ? [{ label: `${linked.name} · ${FACTORY_STATUS_LABEL[linked.status]}`, value: linked.id }]
      : []),
  ]
  // 공장명을 손으로 적은 옛 견적: 목록에 같은 이름이 있으면 잇자고 권한다
  const sameName = form.factoryId ? null : findFactoryByName(form.factoryName, factories)
  const pickFactory = (id: string) => {
    const factory = factories.find((f) => f.id === id)
    if (!factory) {
      const { factoryId: _unlinked, ...rest } = form
      setForm(rest)
      return
    }
    setCertText(factory.certifications.join(', '))
    setForm({
      ...form,
      factoryId: factory.id,
      factoryName: factory.name,
      factoryLocation: factory.region,
      factoryCertifications: factory.certifications,
    })
  }
  const num = (v: string) => Math.max(0, Number(v) || 0)
  const int = (v: string) => Math.max(0, Math.round(Number(v) || 0))
  const onText =
    (key: 'factoryName' | 'factoryLocation' | 'orderUnit' | 'paymentTerms' | 'barcode') =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm({ ...form, [key]: e.target.value })
  return (
    <div
      className="border-kumo-line flex flex-col gap-5 rounded-lg border p-5"
      data-testid="offer-form"
    >
      <div>
        <Text variant="heading">{editing ? '공장 견적 수정' : '새 공장 견적'}</Text>
        <Text variant="secondary" size="sm">
          설명·메모·비용 항목명은 요청자의 언어({language})로 자동 번역돼 저장돼요.
        </Text>
      </div>
      {factories.length > 0 && (
        <div className="flex flex-col gap-2">
          <Select
            label="공장 목록에서 고르기"
            description="요청 분류에 맞는 공장이 위에 있어요. 고르면 공장명·소재지·인증이 채워져요."
            value={form.factoryId ?? ''}
            onValueChange={(v: string | null) => pickFactory(v ?? '')}
            items={factoryItems}
          />
          {sameName && (
            <div className="flex flex-wrap items-center gap-2">
              <Text variant="secondary" size="sm">
                공장 목록의 '{sameName.name}' 과(와) 이름이 같아요.
              </Text>
              <Button variant="ghost" size="sm" onClick={() => pickFactory(sameName.id)}>
                목록의 공장과 잇기
              </Button>
            </div>
          )}
        </div>
      )}
      <Grid variant="2up" gap="sm">
        <GridItem>
          <Input
            label="공장명 *"
            placeholder="예: 한빛식품"
            description="사용자가 앱에서 고르는 이름이에요. 견적서에 그대로 보여요."
            value={form.factoryName}
            onChange={onText('factoryName')}
          />
        </GridItem>
        <GridItem>
          <Input
            label="소재지"
            placeholder="예: 경기 화성"
            description="시·군 단위면 충분해요."
            value={form.factoryLocation}
            onChange={onText('factoryLocation')}
          />
        </GridItem>
        <GridItem>
          <Input
            label="공장 인증 (쉼표로 구분)"
            description="공장이 가진 인증. 없으면 비워 두세요."
            placeholder="HACCP, FSSC 22000"
            value={certText}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setCertText(e.target.value)
              setForm({
                ...form,
                factoryCertifications: e.target.value
                  .split(',')
                  .map((x) => x.trim())
                  .filter(Boolean)
                  .slice(0, 10),
              })
            }}
          />
        </GridItem>
        <GridItem>
          <Input
            label={`제조 단가 (${form.currency}) *`}
            description="제품 1개를 만드는 값(포장 포함, 부가세 제외). 요청서 통화 기준이에요."
            type="number"
            min={0}
            value={String(form.unitPrice)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm({ ...form, unitPrice: num(e.target.value) })
            }
          />
        </GridItem>
        <GridItem>
          <Input
            label="최소 주문 수량 (MOQ)"
            description="공장이 받는 최소 생산 수량(개). 처음에는 사용자의 희망 수량이 들어 있어요."
            type="number"
            min={0}
            value={String(form.moq)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm({ ...form, moq: int(e.target.value) })
            }
          />
        </GridItem>
        <GridItem>
          <Input
            label="납기 (일)"
            description="발주 확정부터 출고까지 걸리는 일수."
            type="number"
            min={0}
            value={String(form.leadTimeDays)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm({ ...form, leadTimeDays: int(e.target.value) })
            }
          />
        </GridItem>
        <GridItem>
          <Input
            label="케이스 입수"
            description="한 박스에 들어가는 개수. 모르면 0."
            type="number"
            min={0}
            value={String(form.casePack)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm({ ...form, casePack: int(e.target.value) })
            }
          />
        </GridItem>
        <GridItem>
          <Input
            label="발주 단위"
            description="몇 개 단위로 주문을 받는지."
            placeholder="예: 1케이스 30개, 직송"
            value={form.orderUnit}
            onChange={onText('orderUnit')}
          />
        </GridItem>
        <GridItem>
          <Input
            label={`참고 판매가 (${form.currency}, 선택)`}
            description="공장·운영자가 권하는 소비자가(세전). 없으면 비워 두세요."
            placeholder={form.currency === 'KRW' ? '예: 5900' : '예: 6.99'}
            type="number"
            min={0}
            value={form.suggestedRetail === null ? '' : String(form.suggestedRetail)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm({
                ...form,
                suggestedRetail: e.target.value === '' ? null : num(e.target.value),
              })
            }
          />
        </GridItem>
        <GridItem>
          <Input
            label="결제 조건"
            description="언제 얼마를 내는지."
            placeholder="예: 발주 시 50%, 완료 시 50%"
            value={form.paymentTerms}
            onChange={onText('paymentTerms')}
          />
        </GridItem>
        <GridItem>
          <Input
            label="부가세율 % (세금 포함가 표시용)"
            description="참고 판매가의 세금 포함가 계산에만 써요. 한국 10, 미국 0."
            type="number"
            min={0}
            max={30}
            value={String(form.taxRatePct)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm({ ...form, taxRatePct: Math.min(30, num(e.target.value)) })
            }
          />
        </GridItem>
        <GridItem>
          <Input
            label="견적 유효기한"
            description="이 날짜까지 이 단가가 유효해요. 보통 견적일 + 30일."
            type="date"
            value={form.validUntil ?? ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm({ ...form, validUntil: e.target.value || null })
            }
          />
        </GridItem>
        <GridItem>
          <Input
            label="바코드 (JAN/UPC, 선택)"
            description="발급된 뒤에 입력해요. 견적 단계에서는 보통 비워 둬요."
            value={form.barcode}
            onChange={onText('barcode')}
          />
        </GridItem>
        <GridItem>
          {/* Kumo Input 은 기본 폭이 고정이라 3칸 그리드에서 넘친다 → 칸 폭에 맞춘다 */}
          <div className="grid grid-cols-3 gap-2">
            {(['w', 'd', 'h'] as const).map((k) => (
              <Input
                key={k}
                className="w-full min-w-0"
                placeholder={{ w: '65', d: '65', h: '140' }[k]}
                label={{ w: '가로 mm', d: '세로 mm', h: '높이 mm' }[k]}
                type="number"
                min={0}
                value={String(form.dimensionsMm?.[k] ?? '')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const cur = form.dimensionsMm ?? { w: 0, d: 0, h: 0 }
                  const next = { ...cur, [k]: int(e.target.value) }
                  setForm({
                    ...form,
                    dimensionsMm: next.w === 0 && next.d === 0 && next.h === 0 ? null : next,
                  })
                }}
              />
            ))}
          </div>
        </GridItem>
      </Grid>

      <div className="flex flex-col gap-3">
        <div>
          <Text variant="heading">샘플</Text>
          <Text variant="secondary" size="sm">
            고르기 전에 맛을 볼 수 있는지 사용자가 비교표에서 봐요. 샘플이 없는 공장이면 앱이 첫
            생산분 시식을 안내해요. 조건 설명은 아래 메모에 적어 주세요.
          </Text>
        </div>
        <Grid variant="3up" gap="sm">
          <GridItem>
            <Select
              label="샘플 제공"
              className="w-full"
              value={form.sample?.availability ?? ''}
              onValueChange={(v: string | null) =>
                setForm({
                  ...form,
                  sample: v
                    ? {
                        availability: v as QuoteSampleAvailability,
                        price: v === 'paid' ? (form.sample?.price ?? null) : null,
                        leadTimeDays: v === 'none' ? null : (form.sample?.leadTimeDays ?? null),
                      }
                    : null,
                })
              }
              items={SAMPLE_ITEMS}
            />
          </GridItem>
          {form.sample?.availability === 'paid' && (
            <GridItem>
              <Input
                label={`샘플 비용 (${form.currency})`}
                description="모르면 비워 두세요."
                type="number"
                min={0}
                value={form.sample.price === null ? '' : String(form.sample.price)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  form.sample &&
                  setForm({
                    ...form,
                    sample: {
                      ...form.sample,
                      price: e.target.value === '' ? null : num(e.target.value),
                    },
                  })
                }
              />
            </GridItem>
          )}
          {form.sample && form.sample.availability !== 'none' && (
            <GridItem>
              <Input
                label="샘플까지 걸리는 날 (일)"
                description="모르면 비워 두세요."
                type="number"
                min={0}
                max={365}
                value={form.sample.leadTimeDays === null ? '' : String(form.sample.leadTimeDays)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  form.sample &&
                  setForm({
                    ...form,
                    sample: {
                      ...form.sample,
                      leadTimeDays:
                        e.target.value === '' ? null : Math.min(365, int(e.target.value)),
                    },
                  })
                }
              />
            </GridItem>
          )}
        </Grid>
      </div>

      <div className="flex flex-col gap-3">
        <div>
          <Text variant="heading">기타 비용</Text>
          <Text variant="secondary" size="sm">
            단가 외에 드는 비용을 빈도별로 (개발비, 테스트 배치, 라벨 동판, 보관, 인증…). 자주 쓰는
            항목은 아래 버튼으로 추가하세요.
          </Text>
        </div>
        <div className="flex flex-wrap gap-2">
          {LINE_PRESETS.map((p) => (
            <Button
              key={p.label}
              variant="secondary"
              size="sm"
              icon={<Plus />}
              disabled={form.lines.length >= 20}
              onClick={() =>
                setForm({
                  ...form,
                  lines: [...form.lines, { label: p.label, amount: 0, frequency: p.frequency }],
                })
              }
            >
              {p.label}
            </Button>
          ))}
        </div>
        {form.lines.map((line, i) => (
          <div key={i} className="flex flex-wrap items-end gap-2">
            <div className="min-w-48 flex-1">
              <Input
                aria-label="항목명"
                placeholder="항목명"
                value={line.label}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setForm({
                    ...form,
                    lines: form.lines.map((l, j) =>
                      j === i ? { ...l, label: e.target.value } : l,
                    ),
                  })
                }
              />
            </div>
            <div className="w-36">
              <Input
                aria-label="금액"
                type="number"
                min={0}
                value={String(line.amount)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setForm({
                    ...form,
                    lines: form.lines.map((l, j) =>
                      j === i ? { ...l, amount: num(e.target.value) } : l,
                    ),
                  })
                }
              />
            </div>
            <Select
              aria-label="빈도"
              className="min-w-28"
              value={line.frequency}
              onValueChange={(v: string | null) =>
                v &&
                setForm({
                  ...form,
                  lines: form.lines.map((l, j) =>
                    j === i ? { ...l, frequency: v as QuoteFeeFrequency } : l,
                  ),
                })
              }
              items={FREQ_ITEMS}
            />
            <Button
              variant="ghost"
              shape="square"
              icon={<Trash />}
              aria-label="항목 삭제"
              onClick={() => setForm({ ...form, lines: form.lines.filter((_, j) => j !== i) })}
            />
          </div>
        ))}
        {form.lines.length < 20 && (
          <div>
            <Button
              variant="ghost"
              size="sm"
              icon={<Plus />}
              onClick={() =>
                setForm({
                  ...form,
                  lines: [...form.lines, { label: '', amount: 0, frequency: 'one_time' }],
                })
              }
            >
              직접 추가
            </Button>
          </div>
        )}
      </div>

      <Textarea
        label="제품 설명 (견적서에 표시)"
        value={form.description}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          setForm({ ...form, description: e.target.value })
        }
      />
      <Textarea
        label="요청자에게 남길 메모"
        value={form.notes}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          setForm({ ...form, notes: e.target.value })
        }
      />
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="primary"
          icon={<PaperPlaneTilt />}
          loading={busy}
          disabled={form.unitPrice <= 0 || !form.factoryName.trim()}
          onClick={onSave}
        >
          {editing ? '수정 저장' : '견적 보내기'}
        </Button>
        <Button variant="ghost" disabled={busy} onClick={onCancel}>
          취소
        </Button>
        {(form.unitPrice <= 0 || !form.factoryName.trim()) && (
          <Text variant="secondary" size="sm">
            공장명과 제조 단가는 필수예요.
          </Text>
        )}
      </div>
    </div>
  )
}
